/**
 * Color management capabilities for RGBW lights
 * Provides color control actions, conditions, and triggers
 */

/**
 * Create setColor action for RGB color control
 */
export function createSetColorAction() {
	return {
		label: 'Set RGB Color',
		description: 'Set RGB color values',
		channels: ['light:0'],
		parameters: [
			{
				name: 'red',
				label: 'Red Component',
				type: 'number',
				min: 0,
				max: 255,
				description: 'Red color value (0-255)',
			},
			{
				name: 'green',
				label: 'Green Component',
				type: 'number',
				min: 0,
				max: 255,
				description: 'Green color value (0-255)',
			},
			{
				name: 'blue',
				label: 'Blue Component',
				type: 'number',
				min: 0,
				max: 255,
				description: 'Blue color value (0-255)',
			},
			{
				name: 'white',
				label: 'White Component',
				type: 'number',
				min: 0,
				max: 255,
				description: 'White color value (0-255)',
				optional: true,
			},
			{
				name: 'transition',
				label: 'Transition Duration',
				type: 'number',
				min: 0.1,
				max: 30.0,
				unit: 's',
				description: 'Color transition duration in seconds',
				optional: true,
			},
		],
		generateCommand: (deviceId, channel, params) => ({
			id: 1,
			method: 'Light.Set',
			params: {
				id: parseInt(channel.split(':')[1]),
				on: true,
				red: params.red,
				green: params.green,
				blue: params.blue,
				...(params.white !== undefined && { white: params.white }),
				...(params.transition && { transition_duration: params.transition }),
			},
		}),
	};
}

/**
 * Create setColorHex action for hex color control
 */
export function createSetColorHexAction() {
	return {
		label: 'Set Color (Hex)',
		description: 'Set color using hex value',
		channels: ['light:0'],
		parameters: [
			{
				name: 'hex',
				label: 'Hex Color',
				type: 'string',
				description: 'Hex color value (e.g., #FF0000 for red)',
				pattern: '^#[0-9A-Fa-f]{6}$',
			},
			{
				name: 'white',
				label: 'White Component',
				type: 'number',
				min: 0,
				max: 255,
				description: 'White color value (0-255)',
				optional: true,
			},
			{
				name: 'transition',
				label: 'Transition Duration',
				type: 'number',
				min: 0.1,
				max: 30.0,
				unit: 's',
				description: 'Color transition duration in seconds',
				optional: true,
			},
		],
		generateCommand: (deviceId, channel, params) => {
			// Convert hex to RGB
			const hex = params.hex.replace('#', '');
			const red = parseInt(hex.substr(0, 2), 16);
			const green = parseInt(hex.substr(2, 2), 16);
			const blue = parseInt(hex.substr(4, 2), 16);

			return {
				id: 1,
				method: 'Light.Set',
				params: {
					id: parseInt(channel.split(':')[1]),
					on: true,
					red,
					green,
					blue,
					...(params.white !== undefined && { white: params.white }),
					...(params.transition && { transition_duration: params.transition }),
				},
			};
		},
	};
}

/**
 * Create setEffect action for light effects
 */
export function createSetEffectAction() {
	return {
		label: 'Set Light Effect',
		description: 'Apply predefined light effect',
		channels: ['light:0'],
		parameters: [
			{
				name: 'effect',
				label: 'Effect Type',
				type: 'select',
				options: [
					{ value: 0, label: 'Off' },
					{ value: 1, label: 'Meteor Shower' },
					{ value: 2, label: 'Gradual Change' },
					{ value: 3, label: 'Flash' },
					{ value: 4, label: 'Breath' },
					{ value: 5, label: 'On/Off Gradual' },
					{ value: 6, label: 'Red/Green Gradual' },
				],
				description: 'Light effect to apply',
			},
		],
		generateCommand: (deviceId, channel, params) => ({
			id: 1,
			method: 'Light.Set',
			params: {
				id: parseInt(channel.split(':')[1]),
				on: true,
				effect: params.effect,
			},
		}),
	};
}

/**
 * Create colorMatches condition for color checking
 */
export function createColorMatchesCondition() {
	return {
		label: 'Color Matches',
		description: 'Check if current color matches specified RGB values',
		channels: ['light:0'],
		parameters: [
			{
				name: 'red',
				label: 'Red Component',
				type: 'number',
				min: 0,
				max: 255,
				description: 'Expected red color value (0-255)',
			},
			{
				name: 'green',
				label: 'Green Component',
				type: 'number',
				min: 0,
				max: 255,
				description: 'Expected green color value (0-255)',
			},
			{
				name: 'blue',
				label: 'Blue Component',
				type: 'number',
				min: 0,
				max: 255,
				description: 'Expected blue color value (0-255)',
			},
			{
				name: 'tolerance',
				label: 'Color Tolerance',
				type: 'number',
				min: 0,
				max: 50,
				description: 'Allowed difference per color component (default: 10)',
				optional: true,
				defaultValue: 10,
			},
		],
		evaluateCondition: (deviceState, channel, params) => {
			const lightId = parseInt(channel.split(':')[1]);
			const lightState = deviceState.status?.['light:' + lightId];

			if (!lightState) return false;

			const currentRed = lightState.red || 0;
			const currentGreen = lightState.green || 0;
			const currentBlue = lightState.blue || 0;

			const tolerance = params.tolerance || 10;

			const redMatch = Math.abs(currentRed - params.red) <= tolerance;
			const greenMatch = Math.abs(currentGreen - params.green) <= tolerance;
			const blueMatch = Math.abs(currentBlue - params.blue) <= tolerance;

			return redMatch && greenMatch && blueMatch;
		},
	};
}

/**
 * Create effectActive condition for effect checking
 */
export function createEffectActiveCondition() {
	return {
		label: 'Effect Active',
		description: 'Check if a specific light effect is active',
		channels: ['light:0'],
		parameters: [
			{
				name: 'effect',
				label: 'Effect Type',
				type: 'select',
				options: [
					{ value: 0, label: 'Off' },
					{ value: 1, label: 'Meteor Shower' },
					{ value: 2, label: 'Gradual Change' },
					{ value: 3, label: 'Flash' },
					{ value: 4, label: 'Breath' },
					{ value: 5, label: 'On/Off Gradual' },
					{ value: 6, label: 'Red/Green Gradual' },
				],
				description: 'Expected active effect',
			},
		],
		evaluateCondition: (deviceState, channel, params) => {
			const lightId = parseInt(channel.split(':')[1]);
			const lightState = deviceState.status?.['light:' + lightId];
			return lightState?.effect === params.effect;
		},
	};
}

/**
 * Create colorChanged trigger for color change detection
 */
export function createColorChangedTrigger() {
	return {
		label: 'Color Changed',
		description: 'Triggered when RGB color values change',
		channels: ['light:0'],
		parameters: [
			{
				name: 'threshold',
				label: 'Change Threshold',
				type: 'number',
				min: 1,
				max: 100,
				description: 'Minimum color change to trigger (default: 20)',
				optional: true,
				defaultValue: 20,
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const lightId = parseInt(channel.split(':')[1]);
			const oldLight = oldState?.status?.['light:' + lightId];
			const newLight = newState?.status?.['light:' + lightId];

			if (!oldLight || !newLight) return false;

			const oldRed = oldLight.red || 0;
			const oldGreen = oldLight.green || 0;
			const oldBlue = oldLight.blue || 0;

			const newRed = newLight.red || 0;
			const newGreen = newLight.green || 0;
			const newBlue = newLight.blue || 0;

			const threshold = params.threshold || 20;

			const redChange = Math.abs(newRed - oldRed);
			const greenChange = Math.abs(newGreen - oldGreen);
			const blueChange = Math.abs(newBlue - oldBlue);

			return redChange >= threshold || greenChange >= threshold || blueChange >= threshold;
		},
	};
}

/**
 * Create effectChanged trigger for effect change detection
 */
export function createEffectChangedTrigger() {
	return {
		label: 'Effect Changed',
		description: 'Triggered when light effect changes',
		channels: ['light:0'],
		parameters: [
			{
				name: 'to',
				label: 'Changed To Effect',
				type: 'select',
				options: [
					{ value: 'any', label: 'Any effect' },
					{ value: 0, label: 'Off' },
					{ value: 1, label: 'Meteor Shower' },
					{ value: 2, label: 'Gradual Change' },
					{ value: 3, label: 'Flash' },
					{ value: 4, label: 'Breath' },
					{ value: 5, label: 'On/Off Gradual' },
					{ value: 6, label: 'Red/Green Gradual' },
				],
				description: 'Target effect change',
				optional: true,
			},
		],
		detectTrigger: (oldState, newState, channel, params) => {
			const lightId = parseInt(channel.split(':')[1]);
			const oldEffect = oldState?.status?.['light:' + lightId]?.effect;
			const newEffect = newState?.status?.['light:' + lightId]?.effect;

			if (oldEffect === newEffect) return false;

			if (params.to === 'any' || !params.to) return true;
			return newEffect === params.to;
		},
	};
}
